'use client';

// The company's speech policy (BUILD 2 C2-3, C2-6), mounted on the Company
// profile page for owners and admins (`manageSpeechPolicy`): voice mode,
// read-aloud and spoken approvals. GET and PUT /api/v1/company/speech-policy;
// a change saves at once and reaches everyone's voice config on its next
// load (this person's at once). Connected-service actions stay tap-only
// whatever the switches say (R15). Nothing renders for members, or when the
// server has no speech policy.
import { useCompany } from '@/company/context';
import { useNamespaceCopy } from '@/i18n/ns';
import voiceCopy from '@/i18n/ns/voice';
import { useEffect, useId, useRef, useState } from 'react';
import { fetchSpeechPolicy, putSpeechPolicy } from './api';
import type { SpeechPolicy } from './contracts';
import { SwitchRow } from './settings-controls';
import { voiceConfigStore } from './state';

type Status = 'idle' | 'saved' | 'error';

export function SpeechPolicySection() {
  const copy = useNamespaceCopy(voiceCopy);
  const company = useCompany();
  const allowed = company.data?.membership?.permissions?.manageSpeechPolicy === true;
  const [policy, setPolicy] = useState<SpeechPolicy | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [busy, setBusy] = useState(false);
  const titleId = useId();
  const generation = useRef(0);
  const busyRef = useRef(false);
  const scopeKey = `${company.data?.company?.id ?? ''}:${company.data?.membership?.userId ?? ''}`;

  useEffect(() => {
    const token = ++generation.current;
    setPolicy(null);
    setStatus('idle');
    setBusy(false);
    busyRef.current = false;
    if (allowed)
      fetchSpeechPolicy().then(
        (loaded) => token === generation.current && setPolicy(loaded),
        () => token === generation.current && setPolicy(null),
      );
    return () => {
      generation.current += 1;
    };
  }, [allowed, scopeKey]);

  if (!allowed || !policy) return null;
  const save = (patch: Partial<SpeechPolicy>) => {
    if (busyRef.current) return;
    const token = generation.current;
    busyRef.current = true;
    setBusy(true);
    setStatus('idle');
    putSpeechPolicy(patch).then(
      (next) => {
        if (token !== generation.current) return;
        busyRef.current = false;
        setPolicy(next);
        setBusy(false);
        setStatus('saved');
        void voiceConfigStore.refresh();
      },
      () => {
        if (token !== generation.current) return;
        busyRef.current = false;
        setBusy(false);
        setStatus('error');
      },
    );
  };
  return (
    <section
      data-slot="speech-policy"
      aria-labelledby={titleId}
      className="mt-10 flex flex-col gap-6"
    >
      <h2 id={titleId} className="company-section-title">
        {copy.settingsTitle}
      </h2>
      <SwitchRow
        slot="speech-policy-voice-mode"
        label={copy.companyVoiceMode}
        hint={copy.companyVoiceModeHint}
        on={policy.voiceMode}
        disabled={busy}
        onChange={(voiceMode) => save({ voiceMode })}
      />
      <SwitchRow
        slot="speech-policy-spoken-approvals"
        label={copy.companySpokenApprovals}
        hint={copy.companySpokenApprovalsHint}
        on={policy.spokenApprovals}
        disabled={busy}
        onChange={(spokenApprovals) => save({ spokenApprovals })}
      />
      <p role="status" aria-live="polite" className="min-h-5 text-sm">
        {status === 'saved' ? copy.saved : ''}
      </p>
      {status === 'error' && (
        <p role="alert" className="company-error">
          {copy.saveFailed}
        </p>
      )}
    </section>
  );
}
